import React, { useState, useEffect } from 'react'
import {
  Box,
  Container,
  Text,
  Stack,
  Tabs,
  TextInput,
  NumberInput,
  Select,
  Button,
  Group,
  Loader
} from '@mantine/core'
import { useHistory } from 'react-router-dom'
import agent from '../../app/api/agent'
import { CartridgeSpecFormValues, AmmoProductFormValues, CartridgeSpecDto } from '../../app/models/ammo'
import { PagedResult } from '../../app/models/pagedResult'
import { toast } from 'react-toastify'

const AMMO_TYPES = ['Pistol', 'Rifle', 'Shotgun', 'Rimfire']
const BULLET_TYPES = ['FMJ', 'HP', 'SP', 'BTHP', 'Match', 'Other']

const SubmitAmmoPage = () => {
  const history = useHistory()
  const [activeTab, setActiveTab] = useState<string | null>('spec')

  // CartridgeSpec form state
  const [specForm, setSpecForm] = useState<CartridgeSpecFormValues>({
    name: '',
    shortName: '',
    caliber: '',
    type: AMMO_TYPES[0]
  })
  const [specLoading, setSpecLoading] = useState(false)

  // AmmoProduct form state
  const [ammoForm, setAmmoForm] = useState<AmmoProductFormValues>({
    cartridgeSpecId: '',
    brand: '',
    name: '',
    bulletType: BULLET_TYPES[0]
  })
  const [ammoLoading, setAmmoLoading] = useState(false)
  const [cartridgeSpecs, setCartridgeSpecs] = useState<CartridgeSpecDto[]>([])
  const [loadingSpecs, setLoadingSpecs] = useState(false)

  // Load available cartridge specs
  useEffect(() => {
    setLoadingSpecs(true)
    agent.CartridgeSpecs.list({ pageNumber: 1, pageSize: 100 })
      .then((result: PagedResult<CartridgeSpecDto>) => {
        setCartridgeSpecs(result.items)
      })
      .finally(() => setLoadingSpecs(false))
  }, [])

  const handleSubmitSpec = async () => {
    if (!specForm.name || !specForm.shortName || !specForm.caliber) {
      toast.error('Fill in required fields')
      return
    }

    setSpecLoading(true)
    try {
      const result = await agent.CartridgeSpecs.create(specForm)
      toast.success('Cartridge spec submitted!')
      history.push(`/ammo/specs/${result.id}`)
    } catch (error) {
      toast.error('Failed to submit')
    } finally {
      setSpecLoading(false)
    }
  }

  const handleSubmitAmmo = async () => {
    if (!ammoForm.cartridgeSpecId || !ammoForm.brand || !ammoForm.name || ammoForm.grainWeight === undefined) {
      toast.error('Fill in required fields')
      return
    }

    setAmmoLoading(true)
    try {
      const result = await agent.AmmoProducts.create(ammoForm)
      toast.success('Ammo product submitted!')
      history.push(`/ammo/products/${result.id}`)
    } catch (error) {
      toast.error('Failed to submit')
    } finally {
      setAmmoLoading(false)
    }
  }

  return (
    <Box sx={{ background: 'var(--gs-bg)', minHeight: '100vh', paddingTop: '24px', paddingBottom: '24px' }}>
      <Container size="md">
        <Stack spacing="md">
          {/* Back Link */}
          <Button
            variant="subtle"
            onClick={() => history.push('/ammo')}
            sx={{ width: 'fit-content', paddingLeft: 0 }}
          >
            ← Back to Ammo Registry
          </Button>

          {/* Header */}
          <Box>
            <Text sx={{ fontSize: '28px', fontWeight: 700, marginBottom: '4px' }}>
              Submit Ammo
            </Text>
            <Text sx={{ color: 'var(--gs-muted)' }}>
              Contribute to the ammo registry.
            </Text>
          </Box>

          {/* Tabs */}
          <Tabs value={activeTab} onTabChange={setActiveTab}>
            <Tabs.List>
              <Tabs.Tab value="spec">New Cartridge Spec</Tabs.Tab>
              <Tabs.Tab value="product">New Ammo Load</Tabs.Tab>
            </Tabs.List>

            <Tabs.Panel value="spec" pt="md">
              <Stack spacing="md">
                <TextInput
                  label="Name (required)"
                  placeholder="e.g. 9mm Luger"
                  value={specForm.name}
                  onChange={(e) => setSpecForm({ ...specForm, name: e.currentTarget.value })}
                />
                <TextInput
                  label="Short Name (required)"
                  placeholder="e.g. 9mm"
                  value={specForm.shortName}
                  onChange={(e) => setSpecForm({ ...specForm, shortName: e.currentTarget.value })}
                />
                <TextInput
                  label="Caliber (required)"
                  placeholder="e.g. 9x19mm"
                  value={specForm.caliber}
                  onChange={(e) => setSpecForm({ ...specForm, caliber: e.currentTarget.value })}
                />
                <Select
                  label="Type"
                  data={AMMO_TYPES}
                  value={specForm.type}
                  onChange={(val) => setSpecForm({ ...specForm, type: val || AMMO_TYPES[0] })}
                />
                <Button
                  onClick={handleSubmitSpec}
                  loading={specLoading}
                  fullWidth
                >
                  Submit Cartridge Spec
                </Button>
              </Stack>
            </Tabs.Panel>

            <Tabs.Panel value="product" pt="md">
              <Stack spacing="md">
                <Select
                  label="Cartridge Spec (required)"
                  placeholder="Select a cartridge..."
                  searchable
                  disabled={loadingSpecs}
                  data={cartridgeSpecs.map((spec) => ({
                    value: spec.id,
                    label: `${spec.name} (${spec.caliber})`
                  }))}
                  value={ammoForm.cartridgeSpecId}
                  onChange={(val) => setAmmoForm({ ...ammoForm, cartridgeSpecId: val || '' })}
                />
                <TextInput
                  label="Manufacturer (required)"
                  placeholder="e.g. Federal"
                  value={ammoForm.brand}
                  onChange={(e) => setAmmoForm({ ...ammoForm, brand: e.currentTarget.value })}
                />
                <TextInput
                  label="Product Line (required)"
                  placeholder="e.g. Champion"
                  value={ammoForm.name}
                  onChange={(e) => setAmmoForm({ ...ammoForm, name: e.currentTarget.value })}
                />
                <NumberInput
                  label="Bullet Weight gr (required)"
                  placeholder="e.g. 124"
                  value={ammoForm.grainWeight}
                  onChange={(val) => setAmmoForm({ ...ammoForm, grainWeight: val })}
                />
                <Select
                  label="Bullet Type"
                  data={BULLET_TYPES}
                  value={ammoForm.bulletType}
                  onChange={(val) => setAmmoForm({ ...ammoForm, bulletType: val || BULLET_TYPES[0] })}
                />
                <Button
                  onClick={handleSubmitAmmo}
                  loading={ammoLoading}
                  fullWidth
                >
                  Submit Ammo Product
                </Button>
              </Stack>
            </Tabs.Panel>
          </Tabs>
        </Stack>
      </Container>
    </Box>
  )
}

export default SubmitAmmoPage
